/**
 * Utilitários de apresentação da Produção. Datas: texto ISO "YYYY-MM-DD" em
 * UTC puro (igual ao servidor, functions/lib/producao/dates.ts).
 */
import type { Absence, Job, JobStatus, Technician, Van } from "./api"

export function parseIso(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`)
}
export function toIso(d: Date): string {
  return d.toISOString().slice(0, 10)
}
export function todayIso(): string {
  // Dia local do utilizador, não UTC.
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}
export function addDays(iso: string, n: number): string {
  const d = parseIso(iso)
  d.setUTCDate(d.getUTCDate() + n)
  return toIso(d)
}
export function isSunday(iso: string): boolean {
  return parseIso(iso).getUTCDay() === 0
}
export function workDays(start: string, duration: number): string[] {
  const out: string[] = []
  let d = start
  while (out.length < Math.max(1, duration || 1)) {
    if (!isSunday(d)) out.push(d)
    d = addDays(d, 1)
  }
  return out
}
export function mondayOf(iso: string): string {
  const wd = parseIso(iso).getUTCDay()
  return addDays(iso, wd === 0 ? -6 : 1 - wd)
}
/** Segunda a sábado da semana de `monday`. */
export function weekDays(monday: string): string[] {
  return [0, 1, 2, 3, 4, 5].map((i) => addDays(monday, i))
}

const WD = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"]
const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"]

export function fmtDay(iso: string): string {
  const d = parseIso(iso)
  return `${WD[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
}
export function fmtShort(iso: string): string {
  const d = parseIso(iso)
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
}
export function fmtEur(v: string | number | null | undefined): string {
  if (v == null || v === "") return "—"
  const n = Number(v)
  if (!Number.isFinite(n)) return "—"
  return n.toLocaleString("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
}

export const STATUS_LABEL: Record<JobStatus, string> = {
  por_atribuir: "Por atribuir",
  planeada: "Planeada",
  execucao: "Em execução",
  parada: "Parada",
  concluida: "Concluída",
  cancelada: "Cancelada",
}

export const KIND_SHORT: Record<string, string> = {
  solar: "Solar",
  ac: "AC",
  bc_aqs: "BC AQS",
  bc_aq: "BC Aquec.",
}

export function kindText(job: Pick<Job, "kind" | "kind_summary">): string {
  return job.kind_summary || (job.kind ? KIND_SHORT[job.kind] ?? job.kind : "Instalação")
}

export function initialsOf(t: Pick<Technician, "name" | "initials">): string {
  if (t.initials?.trim()) return t.initials.trim().toUpperCase()
  const parts = t.name.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1]![0] : "")).toUpperCase()
}

/** Dias da obra (vazio sem data). */
export function jobDays(job: Pick<Job, "scheduled_date" | "duration_days">): string[] {
  return job.scheduled_date ? workDays(job.scheduled_date, job.duration_days) : []
}

export function isAbsent(techId: string, day: string, absences: Absence[]): Absence | undefined {
  return absences.find((a) => a.technician_id === techId && day >= a.date_from && day <= a.date_to)
}

/** Técnicos da obra (chefe primeiro). */
export function teamOf(job: Job, technicians: Technician[]): Technician[] {
  const ids = [job.lead_technician_id, ...job.technician_ids].filter((x): x is string => !!x)
  return [...new Set(ids)]
    .map((id) => technicians.find((t) => t.id === id))
    .filter((t): t is Technician => !!t)
}

export function vanOf(job: Job, vans: Van[]): Van | undefined {
  return vans.find((v) => v.id === job.van_id)
}

/** Técnico da obra ausente num dos seus dias? (risco) */
export function hasAbsentTech(job: Job, absences: Absence[]): boolean {
  const days = jobDays(job)
  const ids = [job.lead_technician_id, ...job.technician_ids].filter((x): x is string => !!x)
  return ids.some((id) => days.some((d) => isAbsent(id, d, absences)))
}

/** Uma das 10 cores de etiqueta do Trello, para escolher a cor da carrinha. */
export const TRELLO_COLORS: Array<{ name: string; hex: string }> = [
  { name: "verde", hex: "#4bce97" },
  { name: "amarelo", hex: "#e2b203" },
  { name: "laranja", hex: "#fea362" },
  { name: "vermelho", hex: "#f87168" },
  { name: "roxo", hex: "#9f8fef" },
  { name: "azul", hex: "#579dff" },
  { name: "azul-céu", hex: "#6cc3e0" },
  { name: "lima", hex: "#94c748" },
  { name: "rosa", hex: "#e774bb" },
  { name: "preto", hex: "#8590a2" },
]

// ─── Contratos do Koortex (modo `host`) ─────────────────────────────────────

/** Colunas do quadro em modo `host`: o estado geral do contrato (+ paradas). */
export type ContractColumn = "sem_contrato" | "por_adjudicar" | "por_planear" | "em_execucao" | "por_auditar" | "parada"

export const CONTRACT_STATE_LABEL: Record<string, string> = {
  sem_contrato: "Sem contrato",
  por_adjudicar: "Por adjudicar",
  por_planear: "Por planear",
  em_execucao: "Em execução",
  por_auditar: "Por certificar",
  em_operacao: "Em operação",
  concluido: "Concluída",
  cancelado: "Cancelada",
  parada: "Parada",
}

/**
 * A coluna da obra no quadro em modo `host`. Parada ganha (o estado geral não
 * muda com a paragem); concluídas e canceladas saem do quadro (null).
 */
export function contractColumn(job: Pick<Job, "contract" | "contract_id">): ContractColumn | null {
  const c = job.contract
  if (!c) return "sem_contrato"
  if (c.status === "cancelled" || c.general_state === "cancelado") return null
  if (c.on_hold_since) return "parada"
  switch (c.general_state) {
    case "por_adjudicar":
    case "por_planear":
    case "em_execucao":
    case "por_auditar":
      return c.general_state
    default:
      return null
  }
}

/** Motivo da paragem a mostrar: a do contrato (modo `host`) ou a local. */
export function holdReasonOf(job: Job, hostMode: boolean): string | null {
  if (hostMode) return job.contract?.on_hold_since ? job.contract.hold_reason ?? job.stop_reason ?? "sem motivo" : null
  return job.status === "parada" ? job.stop_reason : null
}

/** Parada: no contrato (modo `host`) ou no estado local. */
export function isHeld(job: Job, hostMode: boolean): boolean {
  return hostMode ? !!job.contract?.on_hold_since : job.status === "parada"
}

/** A data do contrato difere da atribuição na app (mudada no Koortex)? */
export function contractDateDrift(job: Job): string | null {
  const c = job.contract
  if (!c?.planned_start_at) return null
  const start = c.planned_start_at.slice(0, 10)
  return start !== job.scheduled_date ? start : null
}

/** «Ver no Koortex»: a ficha do contrato no CRM (caminho para o `navigate.crm`). */
export function koortexContractHref(contractId: string, locale?: string | null): string {
  return `/${locale || "pt"}/contracts/${encodeURIComponent(contractId)}`
}

/** O técnico comunicou uma paragem que o gestor ainda não resolveu (suspender ou dispensar)? */
export function hasFieldStop(job: Pick<Job, "field_stop_at" | "field_stop_resolved_at">): boolean {
  return !!job.field_stop_at && !job.field_stop_resolved_at
}
